/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import { api, getCurrentUser } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Edit2, Trash2, ShieldCheck, Shield, Eye, Users as UsersIcon, KeyRound, Car, Bike, Loader2, Store, Link2, Copy, Check, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const AREA_META = {
  batcave: { label: 'La Batcave', desc: 'Supervision Stripe (soldes, virements, paiements)', icon: ShieldCheck },
  batmobile: { label: 'La Batmobile', desc: 'Rotation des Stripe (plafonds de CA, bascule)', icon: Car },
  batcycle: { label: 'Le BatCycle', desc: 'Coffre de tokens & prélèvements de masse', icon: Bike },
};

function AreaPinSettings() {
  const [status, setStatus] = useState(null);
  const [pins, setPins] = useState({ batcave: '', batmobile: '', batcycle: '' });
  const [busy, setBusy] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/auth/area-pins');
      setStatus(r.data);
    } catch {
      setStatus({});
    }
  };
  useEffect(() => { load(); }, []);

  const setPin = async (area) => {
    const pin = pins[area];
    if (!/^\d{6}$/.test(pin)) { toast.error('PIN à 6 chiffres requis'); return; }
    setBusy(area);
    try {
      await api.put('/auth/area-pin', { area, pin });
      toast.success(`Code ${AREA_META[area].label} enregistré`);
      setPins((p) => ({ ...p, [area]: '' }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setBusy(''); }
  };

  const clearPin = async (area) => {
    if (!confirm(`Réinitialiser le code ${AREA_META[area].label} ? Il faudra alors votre PIN de connexion pour y accéder.`)) return;
    setBusy(area);
    try {
      await api.put('/auth/area-pin', { area, pin: '' });
      toast.success('Code réinitialisé (retour au PIN de connexion)');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally { setBusy(''); }
  };

  return (
    <Card className="card-flat p-6 mt-8" data-testid="area-pin-settings">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="h-4 w-4 text-zinc-700" />
        <h2 className="font-display font-bold tracking-tight">Codes d'accès des zones sécurisées</h2>
      </div>
      <p className="text-sm text-zinc-500 mb-5">Définis un PIN à 6 chiffres dédié pour chaque zone. Sans code dédié, ta zone utilise ton PIN de connexion.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {Object.entries(AREA_META).map(([area, m]) => {
          const configured = status?.[area];
          const Icon = m.icon;
          return (
            <div key={area} className="rounded-xl border border-zinc-200 p-4" data-testid={`area-pin-card-${area}`}>
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-md bg-zinc-950 text-white grid place-items-center"><Icon className="h-4 w-4" /></div>
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{m.label}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{m.desc}</div>
                </div>
              </div>
              <div className="mt-2">
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${configured ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-500'}`}>
                  {status == null ? '…' : configured ? 'Code dédié actif' : 'PIN de connexion (par défaut)'}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Input
                  type="text" inputMode="numeric" maxLength={6}
                  value={pins[area]}
                  onChange={(e) => setPins((p) => ({ ...p, [area]: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  className="h-10 rounded-md font-mono tracking-widest text-center"
                  placeholder="••••••"
                  data-testid={`area-pin-input-${area}`}
                />
                <Button onClick={() => setPin(area)} disabled={busy === area} className="rounded-md h-10 bg-zinc-950 shrink-0" data-testid={`area-pin-save-${area}`}>
                  {busy === area ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Définir'}
                </Button>
              </div>
              {configured && (
                <button type="button" onClick={() => clearPin(area)} disabled={busy === area}
                  className="mt-2 text-[11px] text-zinc-400 hover:text-red-600" data-testid={`area-pin-clear-${area}`}>
                  Réinitialiser (revenir au PIN de connexion)
                </button>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

const ROLE_LABELS = {
  super_admin: { label: 'Super Admin', icon: ShieldCheck, desc: 'Accès total · gère utilisateurs + Stripe + tout' },
  manager: { label: 'Manager', icon: Shield, desc: 'Boutiques, produits, transactions · pas de gestion utilisateurs/Stripe' },
  viewer: { label: 'Viewer', icon: Eye, desc: 'Lecture seule (analytics + transactions)' },
  collaborator: { label: 'Collaborateur (accès à la carte)', icon: Wrench, desc: 'Développeur, prestataire… Vous cochez les modules autorisés : il ne voit et n\'utilise que ceux-là (le backend refuse le reste). Il se connecte sur /invite et choisit son PIN via un lien d\'invitation.' },
  guest: { label: 'Invité (propriétaire de boutique)', icon: Store, desc: 'Portail dédié en lecture seule : stats, analytics et CA journalier par compte Stripe/Viva de SES boutiques uniquement. Il choisit son PIN via un lien d\'invitation.' },
};

const emptyForm = { name: '', pin: '', role: 'collaborator', data_scope: 'shared', store_ids: [], permissions: [], disabled: false };
const LEGACY_ROLES = ['manager', 'viewer'];

const InviteLinkDialog = ({ invite, onClose }) => {
  const [copied, setCopied] = useState(false);
  if (!invite) return null;
  const url = `${window.location.origin}/invite/activation/${invite.token}`;
  const copy = async () => { try { await navigator.clipboard.writeText(url); setCopied(true); toast.success('Lien copié'); setTimeout(() => setCopied(false), 1500); } catch { toast.error('Copie impossible, sélectionnez le lien'); } };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="invite-link-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2"><Link2 className="h-5 w-5" /> Lien d'invitation · {invite.name}</DialogTitle>
          <DialogDescription className="text-zinc-500">Envoyez ce lien à la personne : elle choisira son PIN à 6 chiffres à la première ouverture, puis se connectera avec ce PIN sur <span className="font-mono">/invite</span>. Lien à usage unique, valable 7 jours.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input readOnly value={url} onFocus={(e) => e.target.select()} className="h-10 rounded-md font-mono text-xs" data-testid="invite-link-input" />
          <Button onClick={copy} className="btn-primary rounded-md shrink-0" data-testid="invite-link-copy">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button>
        </div>
        <DialogFooter><Button variant="outline" className="rounded-md" onClick={onClose} data-testid="invite-link-close">Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SCOPE_LABELS = {
  shared: { label: 'Partage mes données', desc: 'Voit MES boutiques, produits, comptes — travaille sur les données existantes.' },
  own: { label: 'Espace vierge (privé)', desc: 'Démarre un cahier vide — crée ses propres boutiques/comptes, ne voit jamais les vôtres.' },
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [stores, setStores] = useState([]);
  const [invite, setInvite] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const me = getCurrentUser();
  const isGuest = form.role === 'guest';
  const isCollab = form.role === 'collaborator';
  const invited = isGuest || isCollab;

  const load = async () => {
    const r = await api.get('/users');
    setUsers(r.data);
  };
  useEffect(() => { load(); api.get('/stores', { params: { psp: 'all' } }).then((r) => setStores(r.data)).catch(() => {}); api.get('/users/permissions-catalog').then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, pin: '', role: u.role, data_scope: u.data_scope || 'shared', store_ids: u.store_ids || [], permissions: u.permissions || [], disabled: !!u.disabled }); setOpen(true); };
  const togglePerm = (k) => setForm((f) => ({ ...f, permissions: f.permissions.includes(k) ? f.permissions.filter((x) => x !== k) : [...f.permissions, k] }));
  const toggleStore = (id) => setForm((f) => ({ ...f, store_ids: f.store_ids.includes(id) ? f.store_ids.filter((x) => x !== id) : [...f.store_ids, id] }));
  const regenerateInvite = async (u) => {
    if (u.pin_set && !confirm(`Générer un nouveau lien pour ${u.name} ? Son PIN actuel sera réinitialisé, il en choisira un nouveau.`)) return;
    try { const r = await api.post(`/users/${u.id}/invite`); setInvite({ token: r.data.invite_token, name: u.name }); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Nom requis'); return; }
    if (isGuest && !form.store_ids.length) { toast.error('Choisissez au moins une boutique'); return; }
    if (isCollab && !form.permissions.length) { toast.error('Choisissez au moins un module'); return; }
    if (!editing && !invited && !/^\d{6}$/.test(form.pin)) { toast.error('PIN à 6 chiffres requis'); return; }
    if (form.pin && !/^\d{6}$/.test(form.pin)) { toast.error('PIN doit être 6 chiffres'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, form);
        toast.success('Utilisateur mis à jour');
      } else {
        const r = await api.post('/users', form);
        toast.success(invited ? 'Accès créé — envoyez le lien d\'invitation' : 'Utilisateur créé');
        if (r.data.invite_token) setInvite({ token: r.data.invite_token, name: r.data.name });
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!confirm(`Supprimer ${u.name} ?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success('Utilisateur supprimé');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-5xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline mb-2">— Sécurité</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Utilisateurs & permissions</h1>
          <p className="text-zinc-600 mt-2">Super Admin (entrée /admin) · Collaborateurs à la carte et Invités par boutique (entrée /invite) · PIN unique · sessions révocables.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="admin-add-user-button" className="btn-primary rounded-md">
              <Plus className="h-4 w-4 mr-2" /> Nouvel utilisateur
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">
                {editing ? `Modifier ${editing.name}` : 'Nouvel utilisateur'}
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                Le PIN à 6 chiffres est l'unique credential. Il doit être unique.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">NOM</Label>
                <Input
                  data-testid="admin-user-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 h-10 rounded-md"
                  placeholder="Marie Dupont"
                />
              </div>
              {!(invited && !editing) && <div>
                <Label className="text-xs">PIN À 6 CHIFFRES {editing && '(laisser vide pour ne pas changer)'}</Label>
                <Input
                  data-testid="admin-user-pin-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={form.pin}
                  onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  className="mt-1.5 h-10 rounded-md font-mono tracking-widest text-center text-lg"
                  placeholder="••••••"
                />
              </div>}
              <div>
                <Label className="text-xs">RÔLE</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="admin-user-role-select" className="mt-1.5 h-10 rounded-md">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_LABELS).filter(([k]) => !LEGACY_ROLES.includes(k) || k === form.role).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-zinc-500 mt-1.5">{ROLE_LABELS[form.role]?.desc}</p>
              </div>
              {isGuest && (
                <div data-testid="admin-user-stores-block">
                  <Label className="text-xs">BOUTIQUES AUTORISÉES ({form.store_ids.length})</Label>
                  <div className="mt-1.5 max-h-44 overflow-y-auto border border-zinc-200 rounded-md divide-y divide-zinc-100">
                    {stores.length === 0 ? <div className="p-3 text-xs text-zinc-400">Aucune boutique.</div> : stores.map((s) => (
                      <label key={s.id} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-zinc-50" data-testid={`admin-user-store-${s.id}`}>
                        <input type="checkbox" checked={form.store_ids.includes(s.id)} onChange={() => toggleStore(s.id)} className="accent-zinc-950" />
                        <span className="truncate">{s.display_name || s.name}</span>
                      </label>
                    ))}
                  </div>
                  {!editing && <p className="text-xs text-zinc-500 mt-1.5">Un lien d'invitation sera généré : le propriétaire y choisit lui-même son PIN.</p>}
                  {editing && (
                    <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer" data-testid="admin-user-disabled-toggle">
                      <input type="checkbox" checked={form.disabled} onChange={(e) => setForm({ ...form, disabled: e.target.checked })} className="accent-red-600" />
                      <span className={form.disabled ? 'text-red-600 font-medium' : ''}>Accès désactivé (connexion bloquée)</span>
                    </label>
                  )}
                </div>
              )}
              {isCollab && (
                <div data-testid="admin-user-perms-block">
                  <Label className="text-xs">MODULES AUTORISÉS ({form.permissions.length})</Label>
                  <div className="mt-1.5 max-h-56 overflow-y-auto border border-zinc-200 rounded-md divide-y divide-zinc-100">
                    {catalog.map((m) => (
                      <label key={m.key} className="flex items-start gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-zinc-50" data-testid={`admin-user-perm-${m.key}`}>
                        <input type="checkbox" checked={form.permissions.includes(m.key)} onChange={() => togglePerm(m.key)} className="accent-zinc-950 mt-1" />
                        <span className="min-w-0"><span className="block font-medium">{m.label}</span><span className="block text-[11px] text-zinc-500">{m.description}</span></span>
                      </label>
                    ))}
                  </div>
                  {!editing && <p className="text-xs text-zinc-500 mt-1.5">Un lien d'invitation sera généré : la personne y choisit elle-même son PIN.</p>}
                  {editing && (
                    <label className="flex items-center gap-2 mt-3 text-sm cursor-pointer" data-testid="admin-user-disabled-toggle">
                      <input type="checkbox" checked={form.disabled} onChange={(e) => setForm({ ...form, disabled: e.target.checked })} className="accent-red-600" />
                      <span className={form.disabled ? 'text-red-600 font-medium' : ''}>Accès désactivé (connexion bloquée, sessions révoquées)</span>
                    </label>
                  )}
                </div>
              )}
              {form.role !== 'super_admin' && !isGuest && (
                <div data-testid="admin-user-scope-block">
                  <Label className="text-xs">DONNÉES / ESPACE DE TRAVAIL</Label>
                  <Select value={form.data_scope} onValueChange={(v) => setForm({ ...form, data_scope: v })}>
                    <SelectTrigger data-testid="admin-user-scope-select" className="mt-1.5 h-10 rounded-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(SCOPE_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1.5">{SCOPE_LABELS[form.data_scope]?.desc}</p>
                  {editing && (
                    <p className="text-[11px] text-amber-600 mt-1">Changer l'espace d'un utilisateur existant ne déplace pas ses données déjà créées.</p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-md" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={save} disabled={saving} data-testid="admin-save-user-button" className="btn-primary rounded-md">
                {editing ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-3">
        {users.length === 0 ? (
          <Card className="card-flat p-10 text-center">
            <UsersIcon className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
            <p className="text-zinc-500 text-sm">Aucun utilisateur.</p>
          </Card>
        ) : users.map((u) => {
          const r = ROLE_LABELS[u.role] || ROLE_LABELS.viewer;
          const Icon = r.icon;
          const isMe = me?.id === u.id;
          return (
            <Card key={u.id} className="card-flat p-5 flex items-center gap-4" data-testid={`user-card-${u.id}`}>
              <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
                u.role === 'super_admin' ? 'bg-zinc-950 text-white' :
                u.role === 'manager' ? 'bg-blue-600 text-white' :
                u.role === 'guest' ? 'bg-amber-500 text-white' :
                u.role === 'collaborator' ? 'bg-violet-600 text-white' :
                'bg-zinc-200 text-zinc-700'
              }`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-display font-bold tracking-tight truncate">{u.name}</div>
                  {isMe && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-mono">VOUS</span>}
                  {u.disabled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-mono" data-testid={`user-disabled-${u.id}`}>DÉSACTIVÉ</span>}
                  {u.role === 'collaborator' && !u.pin_set && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-mono" data-testid={`user-invite-pending-${u.id}`}>PIN EN ATTENTE</span>}
                  {u.role === 'guest' && !u.pin_set && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-mono" data-testid={`user-invite-pending-${u.id}`}>PIN EN ATTENTE</span>}
                </div>
                <div className="text-xs text-zinc-500 flex items-center gap-3 mt-0.5 flex-wrap">
                  <span>{u.role === 'guest' ? 'Invité' : u.role === 'collaborator' ? 'Collaborateur' : r.label}</span>
                  {u.role === 'collaborator' && (
                    <>
                      <span>·</span>
                      <span className="truncate" data-testid={`user-perms-${u.id}`}>{(u.permissions || []).map((k) => catalog.find((c) => c.key === k)?.label || k).join(', ') || 'Aucun module'}</span>
                    </>
                  )}
                  {u.role === 'guest' && (
                    <>
                      <span>·</span>
                      <span className="truncate" data-testid={`user-stores-${u.id}`}>{(u.store_ids || []).map((id) => { const s = stores.find((x) => x.id === id); return s ? (s.display_name || s.name) : null; }).filter(Boolean).join(', ') || 'Aucune boutique'}</span>
                    </>
                  )}
                  {LEGACY_ROLES.includes(u.role) && (
                    <>
                      <span>·</span>
                      <span className={u.data_scope === 'own' ? 'text-indigo-600 font-medium' : ''}>
                        {u.data_scope === 'own' ? 'Espace privé' : 'Données partagées'}
                      </span>
                    </>
                  )}
                  <span>·</span>
                  <span>{u.last_login ? `Dernière connexion ${new Date(u.last_login).toLocaleDateString('fr-FR')}` : 'Jamais connecté'}</span>
                </div>
              </div>
              <div className="flex gap-1">
                {(u.role === 'guest' || u.role === 'collaborator') && (
                  <Button variant="ghost" size="icon" onClick={() => regenerateInvite(u)} data-testid={`user-invite-${u.id}`} className="rounded-md" title="Lien d'invitation">
                    <Link2 className="h-4 w-4" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`} className="rounded-md">
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(u)} disabled={isMe} data-testid={`user-delete-${u.id}`} className="rounded-md text-red-600 hover:text-red-700 disabled:opacity-30">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {me?.role === 'super_admin' && <AreaPinSettings />}
      <InviteLinkDialog invite={invite} onClose={() => setInvite(null)} />
    </div>
  );
}
